import type {
  MaintenancePhase,
  MaintenanceCopyBuilder,
} from '@/types/Maintenance/Maintenance.types';

/** Prefix of the local-storage keys that remember a dismissal per event. */
export const MAINTENANCE_DISMISSAL_STORAGE_PREFIX = 'zd.maintenance';

/** Milliseconds in a minute — converts reminder lead minutes into a timer delay. */
export const MS_PER_MINUTE = 60_000;

/**
 * How often the client re-checks maintenance status while a window is active, so
 * the UI recovers on its own once an admin ends the event (there is no timer for
 * the end — maintenance runs until `is_active` is turned off in the DB).
 */
export const MAINTENANCE_POLL_INTERVAL_MS = 60_000;

/**
 * Largest delay a browser `setTimeout` accepts (signed 32-bit ms, ~24.8 days).
 * Larger values overflow and fire immediately, so timers beyond this are skipped.
 */
export const MAX_TIMEOUT_DELAY_MS = 2_147_483_647;

/** Weight applied to the emphasised runs of maintenance copy. */
export const MAINTENANCE_EMPHASIS_FONT_WEIGHT = 500;

/** How maintenance timestamps are written, e.g. `10:00 PM, Sat 28 Jul`. */
export const MAINTENANCE_TIME_FORMAT = 'h:mm A, ddd D MMM';

/** Fallback reminder lead time when an event omits it (matches the DB default). */
export const DEFAULT_REMINDER_LEAD_MINUTES = 10;

/** Shared between the alert bar and the reminder toast so they never drift. */
export const buildMaintenanceReminderTitle = (leadMinutes: number): string => `Maintenance starts in ${leadMinutes} minutes`;

export const MAINTENANCE_ALERT_BAR_COPY: Record<MaintenancePhase, MaintenanceCopyBuilder> = {
  scheduled: ({ start, end }) => [
    { text: 'Scheduled maintenance: ' },
    { text: `${start} – ${end}`, emphasised: true },
    { text: '. Please save your work before maintenance begins.' },
  ],
  imminent: ({ start, leadMinutes }) => [
    {
      text: buildMaintenanceReminderTitle(leadMinutes ?? DEFAULT_REMINDER_LEAD_MINUTES),
      emphasised: true,
    },
    { text: '. Please save your work now — scheduled start ' },
    { text: `${start}.`, emphasised: true },
  ],
  active: ({ end }) => [
    { text: 'Maintenance in progress — some features may be temporarily unavailable. Expected back by ' },
    { text: `${end}.`, emphasised: true },
  ],
};

export const MAINTENANCE_DEFAULT_DESCRIPTION = "We're upgrading the genomic curation platform "
  + 'so variant review and precision-medicine reporting stay fast and reliable for every child '
  + 'on ZERO. ZeroDash is temporarily unavailable, please check back shortly.';

/** Heading of the first-access pop-up. */
export const MAINTENANCE_MODAL_TITLE = 'Scheduled maintenance';

/** Label on the button that dismisses either notification. */
export const MAINTENANCE_ACKNOWLEDGE_LABEL = 'Got it';

/** Screen-reader label for the reminder's close button. */
export const MAINTENANCE_REMINDER_DISMISS_LABEL = 'Dismiss maintenance reminder';

/** Body of the first-access pop-up. */
export const buildMaintenanceModalCopy: MaintenanceCopyBuilder = ({ start, end }) => [
  { text: 'ZeroDash will be unavailable from ' },
  { text: start, emphasised: true },
  { text: ' to ' },
  { text: end, emphasised: true },
  { text: '. Please save your work before maintenance begins.' },
];

/** Body of the pre-maintenance reminder, broken exactly where the design breaks. */
export const buildMaintenanceReminderCopy: MaintenanceCopyBuilder = ({ start, end }) => [
  { text: 'Please save your work now. ZeroDash will be unavailable during maintenance.' },
  { text: 'Starts at: ', newLine: true },
  { text: start, emphasised: true },
  { text: 'Expected back: ', newLine: true },
  { text: end, emphasised: true },
];
